// Upload-v3-ParallelGallery.js - Parallel Uploads (10 small files) + Gallery Picker
// 
// NEW FEATURES:
// ✅ 10 parallel uploads for files <5MB
// ✅ 1 sequential upload for files ≥5MB  
// ✅ Gallery picker (direct from photo app)
// ✅ Individual progress bars for each file
// ✅ Smart queue management
// ✅ Speed tracking per file
//
// INSTALLATION:
// 1. Replace /var/www/avs-cloud/frontend/src/Upload.js with this file
// 2. npm run build
// 3. Done!

import React, { useState, useEffect, useRef } from 'react';

function Upload({ token, user, onBackToDashboard, teamContext, currentFolderContext }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadPhase, setUploadPhase] = useState({});
  const [uploadSpeed, setUploadSpeed] = useState({});
  const [completedFiles, setCompletedFiles] = useState([]);
  const [activeUploads, setActiveUploads] = useState(0);
  const [uploadMode, setUploadMode] = useState('files'); // 'files' or 'gallery'
  
  const MAX_PARALLEL_SMALL = 150; // Max 150 parallel for <50MB
  const SMALL_FILE_THRESHOLD = 50 * 1024 * 1024; // 50MB
  const uploadQueueRef = useRef([]);
  const activeXHRs = useRef({});
  const selectedFilesRef = useRef([]);  // ← Fix closure issue
  const teamContextRef = useRef(teamContext); // always up-to-date even in stale closures
  useEffect(() => { teamContextRef.current = teamContext; }, [teamContext]);
  const tokenRef = useRef(token); // always use latest token in XHR closures
  useEffect(() => { tokenRef.current = token; }, [token]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const MAX_SIZE = 10 * 1024 * 1024 * 1024; // 10 GB
    const MAX_FILES = 2000; // Max 2000 files at once

    // Check total file count
    if (files.length > MAX_FILES) {
      showToast(`❌ Zu viele Dateien!\n\nMax: ${MAX_FILES} Dateien\nAusgewählt: ${files.length}\n\nBitte weniger Dateien auswählen.`);
      event.target.value = ''; // Reset input
      return;
    }
    
    const validFiles = [];
    
    for (const file of files) {
      if (file.size > MAX_SIZE) {
        showToast(`❌ ${file.name} ist zu groß!\n\nMax: 10 GB\nDatei: ${(file.size / 1024 / 1024 / 1024).toFixed(2)} GB`);
        continue;
      }
      
      validFiles.push({
        id: Date.now() + Math.random(), // Unique ID
        file: file,
        name: file.name,
        size: file.size,
        status: 'pending',
        isSmall: file.size < SMALL_FILE_THRESHOLD
      });
    }
    
    setSelectedFiles(prev => {
      const newFiles = [...prev, ...validFiles];
      selectedFilesRef.current = newFiles;  // ← Sync ref!
      return newFiles;
    });
    
    // Trigger queue after adding files (only if we have files)
    if (validFiles.length > 0) {
      showToast(`${validFiles.length} Datei${validFiles.length > 1 ? 'en werden' : ' wird'} hochgeladen...`, 'info');
      console.log('[DEBUG] Added files:', validFiles.length);
      console.log('[DEBUG] processUploadQueue exists?', typeof processUploadQueue);
      setTimeout(() => {
        console.log('[DEBUG] setTimeout triggered!');
        processUploadQueue();
      }, 100);
    }
  };

  const removeFile = (fileId) => {
    // Cancel upload if active
    if (activeXHRs.current[fileId]) {
      activeXHRs.current[fileId].abort();
      delete activeXHRs.current[fileId];
      setActiveUploads(prev => Math.max(0, prev - 1));
    }
    
    setSelectedFiles(prev => {
      const newFiles = prev.filter(f => f.id !== fileId);
      selectedFilesRef.current = newFiles;  // ← Sync ref!
      return newFiles;
    });
    uploadQueueRef.current = uploadQueueRef.current.filter(id => id !== fileId);
  };

  // Helper to update selectedFiles and sync ref
  const updateFileStatus = (fileId, status) => {
    setSelectedFiles(prev => {
      const newFiles = prev.map(f => f.id === fileId ? { ...f, status } : f);
      selectedFilesRef.current = newFiles;  // ← Sync ref!
      return newFiles;
    });
  };

  const processUploadQueue = () => {
    console.log('[DEBUG] processUploadQueue called!', {
      pending: selectedFilesRef.current.filter(f => f.status === 'pending').length,
      uploading: selectedFilesRef.current.filter(f => f.status === 'uploading').length
    });
    
    const pending = selectedFilesRef.current.filter(f => f.status === 'pending');
    const uploading = selectedFilesRef.current.filter(f => f.status === 'uploading');
    
    // Small files: can run up to 10 parallel
    const smallPending = pending.filter(f => f.isSmall);
    const smallUploading = uploading.filter(f => f.isSmall);
    const smallSlots = MAX_PARALLEL_SMALL - smallUploading.length;
    
    // Large files: only 1 at a time
    const largePending = pending.filter(f => !f.isSmall);
    const largeUploading = uploading.filter(f => !f.isSmall);
    const largeSlots = largeUploading.length === 0 ? 1 : 0;
    
    // Start small files
    for (let i = 0; i < Math.min(smallSlots, smallPending.length); i++) {
      uploadSingleFile(smallPending[i]);
    }
    
    // Start large files
    if (largeSlots > 0 && largePending.length > 0) {
      uploadSingleFile(largePending[0]);
    }
  };


  const uploadSingleFile = async (fileItem) => {
    try {
      // Guard: synchronously lock the file in the ref BEFORE any await to prevent duplicate starts
      const currentInRef = selectedFilesRef.current.find(f => f.id === fileItem.id);
      if (!currentInRef || currentInRef.status !== 'pending') return false;
      selectedFilesRef.current = selectedFilesRef.current.map(f =>
        f.id === fileItem.id ? { ...f, status: 'uploading' } : f
      );

      console.log(`[UPLOAD] Starting ${fileItem.name} (${fileItem.isSmall ? 'small' : 'large'})`);
      setSelectedFiles(prev => {
        const newFiles = prev.map(f => f.id === fileItem.id ? { ...f, status: 'uploading' } : f);
        selectedFilesRef.current = newFiles;
        return newFiles;
      });
      setUploadProgress(prev => ({...prev, [fileItem.id]: { percentage: 0, loaded: 0, total: fileItem.size }}));
      setActiveUploads(prev => prev + 1);

      const formData = new FormData();
      formData.append('file', fileItem.file);
      const effectiveTeam = teamContextRef.current;
      if (effectiveTeam && effectiveTeam.teamSpaceId) {
        formData.append('team_space_id', effectiveTeam.teamSpaceId);
      } else if (currentFolderContext && currentFolderContext.id) {
        formData.append('folder_id', currentFolderContext.id);
      }
      
      let lastLoaded = 0;
      let lastTime = Date.now();
      
      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        activeXHRs.current[fileItem.id] = xhr;

        // Progress Event
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentage = Math.round((e.loaded / e.total) * 100);
            if (percentage >= 99) {
              setUploadPhase(prev => ({...prev, [fileItem.id]: 'Fertig'}));
            }
            
            // Speed Calculation
            const now = Date.now();
            const timeDiff = (now - lastTime) / 1000;
            
            if (timeDiff >= 0.5) {
              const bytesDiff = e.loaded - lastLoaded;
              const speedMBps = (bytesDiff / 1024 / 1024 / timeDiff).toFixed(2);
              
              setUploadSpeed(prev => ({
                ...prev,
                [fileItem.id]: speedMBps
              }));
              
              lastLoaded = e.loaded;
              lastTime = now;
            }
            
            setUploadProgress(prev => ({
              ...prev,
              [fileItem.id]: {
                percentage,
                loaded: e.loaded,
                total: e.total
              }
            }));
          }
        });

        // Load Event
        xhr.addEventListener('load', async () => {
          delete activeXHRs.current[fileItem.id];
          setActiveUploads(prev => Math.max(0, prev - 1));
          
          if (xhr.status === 200) {
            console.log(`[UPLOAD] SUCCESS: ${fileItem.name}`);
            
            let responseData;
            try {
              responseData = JSON.parse(xhr.responseText);
            } catch (e) {
              console.error('[UPLOAD] Parse error:', e);
              throw new Error('Invalid server response');
            }

            if (!responseData.success) {
              throw new Error(responseData.error || 'Upload failed');
            }
            
            setUploadPhase(prev => ({...prev, [fileItem.id]: 'Wird verarbeitet...'}));
            updateFileStatus(fileItem.id, 'complete');
            setCompletedFiles(prev => [...prev, fileItem.id]);
            setUploadProgress(prev => ({
              ...prev,
              [fileItem.id]: { percentage: 100, loaded: fileItem.size, total: fileItem.size }
            }));
            showToast(`${fileItem.name}: Upload abgeschlossen. Datei wird verarbeitet...`, 'success');

            resolve(true);
            
            // Process next in queue

            setTimeout(() => processUploadQueue(), 100);
            
          } else {
            console.error(`[UPLOAD] Failed: HTTP ${xhr.status}`);
            let errorMsg = `Upload fehlgeschlagen: ${xhr.status}`;
            
            try {
              const errorData = JSON.parse(xhr.responseText);
              errorMsg = errorData.error || errorMsg;
              
              // Check for block error
              if (errorData.code === 'UPLOAD_BLOCKED') {
                showToast(errorMsg, 'warning');
              } else if (errorData.code === 'DUPLICATE_FILE' && errorData.existing_file) {
                // Duplicate detected
                const existing = errorData.existing_file;
                const date = new Date(existing.uploaded_at).toLocaleDateString('de-DE');
showToast(`Datei existiert bereits: ${existing.filename}`, 'warning');
              } else {
                showToast(errorMsg);
              }
            } catch (e) {
              showToast(errorMsg);
            }
            
            setSelectedFiles(prev =>
              prev.map(f => f.id === fileItem.id ? { ...f, status: 'error', error: errorMsg } : f)
            );
            resolve(false);

            // Process next in queue

            setTimeout(() => processUploadQueue(), 100);
          }
        });

        // Error Event
        xhr.addEventListener('error', (e) => {
          console.error('[UPLOAD] Network error:', e);
          delete activeXHRs.current[fileItem.id];
          setActiveUploads(prev => Math.max(0, prev - 1));

          const networkErrorMsg = 'Netzwerkfehler beim Upload';
          showToast(networkErrorMsg);
          setSelectedFiles(prev =>
            prev.map(f => f.id === fileItem.id ? { ...f, status: 'error', error: networkErrorMsg } : f)
          );
          resolve(false);
          
          // Process next in queue

          setTimeout(() => processUploadQueue(), 100);
        });

        // Abort Event
        xhr.addEventListener('abort', () => {
          console.log(`[UPLOAD] Aborted: ${fileItem.name}`);
          delete activeXHRs.current[fileItem.id];
          setActiveUploads(prev => Math.max(0, prev - 1));
          resolve(false);
        });

        // Send to backend
        setUploadPhase(prev => ({...prev, [fileItem.id]: 'Hochladen...'}));
        xhr.open('POST', '/api/files/upload');
        xhr.setRequestHeader('Authorization', 'Bearer ' + tokenRef.current);
        xhr.send(formData);
      });

    } catch (error) {
      console.error('[UPLOAD] Error:', error);
      setActiveUploads(prev => Math.max(0, prev - 1));
      showToast('Upload fehlgeschlagen: ' + error.message);
      setSelectedFiles(prev =>
        prev.map(f => f.id === fileItem.id ? { ...f, status: 'error' } : f)
      );
      
      // Process next in queue

      setTimeout(() => processUploadQueue(), 100);
      return false;
    }
  };

  const resetAll = () => {
    // Abort all active uploads
    Object.values(activeXHRs.current).forEach(xhr => xhr.abort());
    activeXHRs.current = {};
    
    setSelectedFiles([]);
    setUploadProgress({});
    setUploadPhase({});
    setUploadSpeed({});
    setCompletedFiles([]);
    setActiveUploads(0);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const allComplete = selectedFiles.length > 0 && selectedFiles.every(f => f.status === 'complete');
  const hasFiles = selectedFiles.length > 0;
  const pendingFiles = selectedFiles.filter(f => f.status === 'pending');
  const uploadingFiles = selectedFiles.filter(f => f.status === 'uploading');

  const handleLogout = () => {
    window.location.reload();
  };

  return (
    <div style={{minHeight: '100vh', fontFamily: 'var(--font-head)', color: 'var(--white)', position: 'relative'}}>
      {/* Toast Notification */}
      {toast && (
        <div onClick={() => setToast(null)} style={{
          position: 'fixed', top: '1rem', left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, maxWidth: '500px', width: '90%', padding: '0.85rem 1.25rem',
          background: toast.type === 'warning' ? 'rgba(251,191,36,0.15)' : toast.type === 'success' ? 'rgba(74,222,128,0.15)' : 'rgba(255,80,80,0.15)',
          border: `1px solid ${toast.type === 'warning' ? 'rgba(251,191,36,0.4)' : toast.type === 'success' ? 'rgba(74,222,128,0.4)' : 'rgba(255,80,80,0.4)'}`,
          borderRadius: '10px', color: toast.type === 'warning' ? '#fbbf24' : toast.type === 'success' ? '#4ade80' : '#ff8888',
          fontSize: '0.85rem', lineHeight: 1.5, cursor: 'pointer', backdropFilter: 'blur(12px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        }}>
          {toast.message}
        </div>
      )}
      {/* ── Header ─────────────────────────────────────────── */}
      <header style={{
        background: 'rgba(4, 10, 20, 0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid var(--border)',
        position: 'sticky', top: 0, zIndex: 40
      }}>
        <div style={{maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem'}}>
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px', gap: '1rem'}}>
            {/* Logo */}
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <img src="/avs-logo.png" alt="AquaV" className="avs-logo-img" />
              <div className="avs-wordmark">
                Aqua<span className="av">V</span>
                <span className="sub">Cloud</span>
              </div>
            </div>

            {/* Nav buttons */}
            <div style={{display: 'flex', gap: '0.6rem', alignItems: 'center'}}>
              <button onClick={onBackToDashboard} className="avs-btn-ghost" style={{padding: '0.55rem 1rem', fontSize: '0.8rem'}}>
                ← Meine Dateien
              </button>
              <button onClick={handleLogout} style={{padding: '0.55rem 1rem', fontSize: '0.8rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--r-md)', color: '#f87171', cursor: 'pointer', fontFamily: 'var(--font-head)', fontWeight: 600}}>
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Folder Context Banner */}
      {!teamContext && currentFolderContext && currentFolderContext.id && (
        <div style={{
          background: 'rgba(18,112,196,0.1)',
          borderBottom: '1px solid rgba(26,180,240,0.25)',
          padding: '0.6rem 1.5rem',
          display: 'flex', alignItems: 'center', gap: '0.75rem'
        }}>
          <span style={{fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--cyan)'}}>📁 Ordner</span>
          <span style={{fontSize: '0.85rem', color: 'var(--white)', fontWeight: 700}}>{currentFolderContext.name}</span>
          <span style={{fontSize: '0.72rem', color: 'var(--muted)', marginLeft: 'auto'}}>Upload wird in diesen Ordner gespeichert</span>
        </div>
      )}

      {/* Team Storage Context Banner */}
      {teamContext && (
        <div style={{
          background: 'rgba(168,85,247,0.1)',
          borderBottom: '1px solid rgba(168,85,247,0.3)',
          padding: '0.6rem 1.5rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
            <span style={{fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#a855f7'}}>👥 Team-Speicher</span>
            <span style={{fontSize: '0.85rem', color: '#dceeff', fontWeight: 700}}>{teamContext.spaceName || teamContext.ownerUsername}</span>
            {!teamContext.isOwner && <span style={{fontSize: '0.72rem', color: '#8892a4'}}>({teamContext.ownerUsername})</span>}
          </div>
          <span style={{fontSize: '0.72rem', color: '#a855f7', fontFamily: 'var(--font-mono)'}}>Upload wird in Team-Speicher gespeichert</span>
        </div>
      )}

      {/* Main Content */}
      <div style={{maxWidth: '1280px', margin: '0 auto', padding: '2rem 1.5rem'}}>
        {/* Upload Mode Tabs */}
        <div style={{display: 'flex', gap: '0.5rem', marginBottom: '1.5rem'}}>
          <button
            onClick={() => setUploadMode('files')}
            style={{
              flex: 1, padding: '0.7rem 1.5rem', borderRadius: 'var(--r-md)', fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.2s', border: '1px solid',
              background: uploadMode === 'files' ? 'linear-gradient(135deg, var(--blue-mid), var(--blue-deep))' : 'transparent',
              borderColor: uploadMode === 'files' ? 'rgba(26,180,240,0.25)' : 'var(--border)',
              color: uploadMode === 'files' ? 'var(--white)' : 'var(--muted)',
              boxShadow: uploadMode === 'files' ? 'var(--shadow-glow)' : 'none'
            }}
          >📁 Dateien</button>

          <button
            onClick={() => setUploadMode('gallery')}
            style={{
              flex: 1, padding: '0.7rem 1.5rem', borderRadius: 'var(--r-md)', fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.2s', border: '1px solid',
              background: uploadMode === 'gallery' ? 'linear-gradient(135deg, var(--blue-mid), var(--blue-deep))' : 'transparent',
              borderColor: uploadMode === 'gallery' ? 'rgba(26,180,240,0.25)' : 'var(--border)',
              color: uploadMode === 'gallery' ? 'var(--white)' : 'var(--muted)',
              boxShadow: uploadMode === 'gallery' ? 'var(--shadow-glow)' : 'none'
            }}
          >🖼 Galerie</button>
        </div>

        {/* ── Upload Area ──────────────────────────────────── */}
        <div style={{position: 'relative', marginBottom: '2rem'}}>
          <div className="avs-card" style={{
            border: '1.5px dashed var(--border-hov)',
            padding: 'clamp(2.5rem, 5vw, 3.5rem)',
            textAlign: 'center',
            transition: 'all 0.2s'
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--cyan)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-hov)'}
          >
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              accept={uploadMode === 'gallery' ? 'image/*,video/*' : undefined}
              capture={uploadMode === 'gallery' ? 'environment' : undefined}
              style={{position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer'}}
              id="file-upload"
            />

            <div style={{pointerEvents: 'none'}}>
              <div style={{fontSize: '4rem', marginBottom: '1rem'}}>{uploadMode === 'gallery' ? '🖼' : '☁'}</div>

              <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 'clamp(1.3rem, 3vw, 1.7rem)', letterSpacing: '-0.03em', color: 'var(--white)', marginBottom: '0.5rem'}}>
                {uploadMode === 'gallery' ? 'Fotos & Videos auswählen' : 'Dateien hochladen'}
              </h3>
              <p style={{color: 'var(--muted)', marginBottom: '1rem', fontSize: '0.9rem', lineHeight: 1.6}}>
                {uploadMode === 'gallery'
                  ? 'Klicke oder wähle aus deiner Galerie'
                  : 'Drag & Drop oder Klicken zum Auswählen'
                }
              </p>
              <p style={{fontSize: '0.75rem', color: 'var(--muted-dark)', fontFamily: 'var(--font-mono)', letterSpacing: '0.06em'}}>
                MAX 10 GB PRO DATEI · {uploadMode === 'gallery' ? 'BILDER & VIDEOS' : 'ALLE DATEITYPEN'}
              </p>

              {selectedFiles.length > 0 && (
                <div style={{marginTop: '1.25rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: 'rgba(26,180,240,0.06)', border: '1px solid var(--border-hov)', borderRadius: 'var(--r-sm)'}}>
                  <span style={{fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--cyan)', letterSpacing: '0.1em'}}>
                    {activeUploads > 0
                      ? `${activeUploads} PARALLEL ${activeUploads === 1 ? 'UPLOAD' : 'UPLOADS'}`
                      : 'BEREIT FÜR PARALLELE UPLOADS'
                    }
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* File List with Individual Progress */}
        {hasFiles && (
          <div style={{display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <div>
                <p className="avs-label">Upload Queue</p>
                <span style={{fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--muted)'}}>
                  {selectedFiles.length} {selectedFiles.length === 1 ? 'Datei' : 'Dateien'}
                </span>
              </div>
              {!allComplete && (
                <button onClick={resetAll} style={{padding: '0.5rem 1rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--r-md)', color: '#f87171', cursor: 'pointer', fontFamily: 'var(--font-head)', fontWeight: 600, fontSize: '0.8rem'}}>
                  ✕ Abbrechen
                </button>
              )}
            </div>

            {/* Queue Summary */}
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '0.5rem'}}>
              <div className="avs-card" style={{padding: '1rem'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                  <div style={{width: '36px', height: '36px', background: 'rgba(18,112,196,0.08)', borderRadius: 'var(--r-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem'}}>⏳</div>
                  <div>
                    <p style={{fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--muted)', letterSpacing: '0.08em', textTransform: 'uppercase'}}>Wartend</p>
                    <p style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.4rem', color: 'var(--cyan)'}}>{pendingFiles.length}</p>
                  </div>
                </div>
              </div>
              <div className="avs-card" style={{padding: '1rem'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                  <div style={{width: '36px', height: '36px', background: 'rgba(26,180,240,0.08)', borderRadius: 'var(--r-md)', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <div style={{width: '18px', height: '18px', border: '2px solid rgba(26,180,240,0.2)', borderTop: '2px solid var(--cyan)', borderRadius: '50%', animation: 'spin 1s linear infinite'}}></div>
                  </div>
                  <div>
                    <p style={{fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--muted)', letterSpacing: '0.08em', textTransform: 'uppercase'}}>Aktiv</p>
                    <p style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.4rem', color: 'var(--cyan)'}}>{uploadingFiles.length}</p>
                  </div>
                </div>
              </div>

              <div className="avs-card" style={{padding: '1rem'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                  <div style={{width: '36px', height: '36px', background: 'rgba(34,197,94,0.08)', borderRadius: 'var(--r-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem'}}>✓</div>
                  <div>
                    <p style={{fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--muted)', letterSpacing: '0.08em', textTransform: 'uppercase'}}>Fertig</p>
                    <p style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.4rem', color: '#4ade80'}}>{completedFiles.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Individual File Progress */}
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.6rem'}}>
              {selectedFiles.map((fileItem) => {
                const progress = uploadProgress[fileItem.id] || { percentage: 0, loaded: 0, total: fileItem.size };
                const speed = uploadSpeed[fileItem.id];

                return (
                  <div
                    key={fileItem.id}
                    className="avs-card"
                    style={{
                      padding: '0.875rem',
                      borderColor: fileItem.status === 'complete' ? 'rgba(74,222,128,0.2)' : fileItem.status === 'error' ? 'rgba(239,68,68,0.2)' : fileItem.status === 'uploading' ? 'var(--border-hov)' : 'var(--border)'
                    }}
                  >
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: fileItem.status !== 'pending' ? '0.75rem' : 0}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0}}>
                        {/* Status Icon */}
                        <div style={{width: '32px', height: '32px', borderRadius: 'var(--r-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1rem',
                          background: fileItem.status === 'complete' ? 'rgba(34,197,94,0.1)' : fileItem.status === 'error' ? 'rgba(239,68,68,0.1)' : fileItem.status === 'uploading' ? 'rgba(26,180,240,0.1)' : 'rgba(18,112,196,0.08)'
                        }}>
                          {fileItem.status === 'complete' ? '✓' : fileItem.status === 'error' ? '✕' : fileItem.status === 'uploading' ? (
                            <div style={{width: '14px', height: '14px', border: '2px solid rgba(26,180,240,0.2)', borderTop: '2px solid var(--cyan)', borderRadius: '50%', animation: 'spin 1s linear infinite'}}></div>
                          ) : '⏳'}
                        </div>

                        {/* File Info */}
                        <div style={{flex: 1, minWidth: 0}}>
                          <p style={{fontWeight: 600, fontSize: '0.85rem', color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={fileItem.name}>{fileItem.name}</p>
                          <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.7rem', color: 'var(--muted)', fontFamily: 'var(--font-mono)', marginTop: '1px'}}>
                            <span>{formatBytes(fileItem.size)}</span>
                            {fileItem.isSmall && <span className="avs-tag">Parallel</span>}
                            {speed && fileItem.status === 'uploading' && <span style={{color: 'var(--cyan)'}}>{speed} MB/s</span>}
                          </div>
                        </div>
                      </div>

                      {/* Error message + Remove Button */}
                      <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0}}>
                        {fileItem.status === 'error' && fileItem.error && (
                          <span style={{fontSize: '0.65rem', color: '#f87171', fontFamily: 'var(--font-mono)', maxWidth: '140px', textAlign: 'right', lineHeight: 1.3}}>{fileItem.error}</span>
                        )}
                        {fileItem.status !== 'complete' && (
                          <button onClick={() => removeFile(fileItem.id)} title="Entfernen" style={{padding: '0.35rem', background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '0.85rem', flexShrink: 0}}>✕</button>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    {fileItem.status !== 'pending' && (
                      <div>
                        <div style={{height: '4px', background: 'rgba(18,112,196,0.1)', borderRadius: '2px', overflow: 'hidden'}}>
                          <div style={{
                            height: '100%', transition: 'width 0.3s ease',
                            width: `${fileItem.status === 'complete' ? 100 : progress.percentage || 0}%`,
                            background: fileItem.status === 'complete' ? 'linear-gradient(90deg, #4ade80, #22c55e)' : fileItem.status === 'error' ? 'linear-gradient(90deg, #f87171, #ef4444)' : 'linear-gradient(90deg, var(--cyan), var(--blue-mid))',
                            borderRadius: '2px'
                          }} />
                        </div>
                        <p style={{fontSize: '0.65rem', color: 'var(--muted)', fontFamily: 'var(--font-mono)', marginTop: '3px', textAlign: 'right'}}>
                          {progress.percentage}% · {formatBytes(progress.loaded)} / {formatBytes(progress.total)}{uploadPhase[fileItem.id] ? ` · ${uploadPhase[fileItem.id]}` : ''}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Success Message */}
        {allComplete && (
          <div style={{textAlign: 'center', padding: '4rem 0'}}>
            <div style={{width: '80px', height: '80px', margin: '0 auto 1.5rem', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem'}}>✓</div>
            <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 'clamp(1.5rem, 4vw, 2rem)', letterSpacing: '-0.03em', color: 'var(--white)', marginBottom: '0.5rem'}}>Upload erfolgreich!</h3>
            <p style={{color: 'var(--muted)', marginBottom: '2rem', fontSize: '0.9rem'}}>
              {selectedFiles.length} {selectedFiles.length === 1 ? 'Datei wurde' : 'Dateien wurden'} hochgeladen
            </p>
            <div style={{display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap'}}>
              <button onClick={resetAll} className="avs-btn-ghost" style={{padding: '0.85rem 2rem'}}>Weitere hochladen</button>
              <button onClick={onBackToDashboard} className="avs-btn" style={{padding: '0.85rem 2rem'}}>→ Zu meinen Dateien</button>
            </div>
          </div>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default Upload;
